import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { GAME_REGISTRY, GameConfig, getGameCurricula } from '../../games/models/GameRegistry';
import FilterBar, { FilterState } from '../../games/components/FilterBar';
import defaultGameImage from '../../../shared/assets/images/games/default-game.png';
import '../../games/components/Games.css';
import { useDemo } from '../providers/DemoProvider';


const ALLOWED_IDS = ['rocket-race', 'flappy-muse', 'chordstruction', 'piano-clicker', 'real-or-ai'];

const PremiumBanner = () => (
  <div style={{
    background: 'linear-gradient(135deg, #FFD700, #FFA000)',
    color: '#333',
    padding: '8px',
    width: '100%',
    textAlign: 'center',
    fontWeight: 900,
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  }}>
    🔒 Premium
  </div>
);

const DemoGames: React.FC = () => {
  const { showLockedModal } = useDemo();
  const [filters, setFilters] = useState<FilterState>({
    curriculum: 'all',
    gameMode: 'all',
    tier: 'all',
    playerCount: 'all',
    musicalElements: [],
    musicalSkills: [],
    searchTerm: '',
    category: 'all',
    elementCategory: 'all'
  });

  const filteredGames = useMemo(() => {
    return Object.values(GAME_REGISTRY).filter(game => {
      if (filters.curriculum !== 'all' && !getGameCurricula(game.id).includes(filters.curriculum)) return false;
      // Filtering logic adapted from production Games.tsx
      if (filters.gameMode === 'group' && (typeof game.playerCount !== 'object' || game.playerCount.max < 3)) return false;
      if (filters.gameMode === 'solo' && typeof game.playerCount === 'object' && game.playerCount.min !== 1) return false;
      if (filters.musicalElements.length > 0 && !filters.musicalElements.some(el => game.musicalElements.includes(el as any))) return false;
      if (filters.musicalSkills.length > 0 && !filters.musicalSkills.some(skill => game.musicalSkills.includes(skill as any))) return false;
      if (filters.searchTerm) {
        const search = filters.searchTerm.toLowerCase();
        return game.title.toLowerCase().includes(search) || game.description.toLowerCase().includes(search);
      }
      return true;
    });
  }, [filters]);

  const getPlayerCountDisplay = (playerCount: { min: number; max: number } | 'single-player') => {
    if (typeof playerCount === 'string') return `👥 1`;
    if (playerCount.min === playerCount.max) return `👥 ${playerCount.min}`;
    return `👥 ${playerCount.min}-${playerCount.max}`;
  };

  const sortedAndFilteredGames = useMemo(() => {
    const playable = filteredGames.filter(game => ALLOWED_IDS.includes(game.id));
    const premium = filteredGames.filter(game => !ALLOWED_IDS.includes(game.id));
    return [...playable, ...premium];
  }, [filteredGames]);

  return (
    <div className="games-page">
      <div className="games-header">
        <h1 className="games-title-blue">Overture</h1>
        <p className="games-subtitle">Choose your musical adventure! This is a free demo with 5/22 of our games.</p>
      </div>

      <FilterBar filters={filters} onFilterChange={setFilters} compact />

      <div className="games-results">
        <div className="results-header">
          <span className="results-count">{filteredGames.length} game{filteredGames.length !== 1 ? 's' : ''} found</span>
        </div>

        <div className="games-grid">
          {sortedAndFilteredGames.map((game) => {
            const isAllowed = ALLOWED_IDS.includes(game.id);
            const Wrapper = isAllowed ? Link : 'div';
            const props: any = {
              key: game.id,
              className: `game-card ${game.status} ${!isAllowed ? 'locked' : ''}`,
              style: { textDecoration: 'none', color: 'inherit', cursor: 'pointer' }
            };

            if (isAllowed) {
              props.to = `/demo/game/${game.id}`;
            } else {
              props.onClick = showLockedModal;
            }
            
            return (
              <Wrapper {...props}>
                <div className="game-image-container">
                  <img
                    src={game.thumbnail}
                    alt={game.title}
                    className="game-image"
                    style={{}}
                    onError={(e) => { (e.target as HTMLImageElement).src = defaultGameImage; }}
                  />
                  {game.like && (
                    <div className="like-banner">
                      <span className="like-banner-text">LIKE: {game.like}</span>
                    </div>
                  )}
                </div>
                {!isAllowed && <PremiumBanner />}
                <div className="game-info">
                  <div className="game-header-info">
                    <h3 className="game-title-blue">{game.title}</h3>
                  </div>
                  <p className="game-description">{game.description}</p>
                  <div className="game-meta-row">
                    <span className="player-count">{getPlayerCountDisplay(game.playerCount)}</span>
                  </div>
                  <div className="game-bubbles-row">
                    {game.musicalElements.map(element => <span key={element} className="bubble element-bubble">{element}</span>)}
                    {game.musicalSkills.map(skill => <span key={skill} className="bubble skill-bubble">{skill}</span>)}
                  </div>
                </div>
              </Wrapper>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DemoGames;
